import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * What a class setup must look like before it can be saved.
 *
 * Mirrors the backend's `classes.validation.ts` — the server is the authority,
 * these rules only save a round trip. Keep the two in step.
 *
 * Every numeric field arrives as the string an `<input>` holds and leaves as a
 * number, so the payload is already in the shape the API takes.
 */

/**
 * The names a class may be given, in teaching order. Mirrors `CLASS_NAMES` in
 * the backend's `classes.validation.ts` — one format for every school, so a
 * roster never mixes "9th", "Std IX" and "Class 9".
 */
export const CLASS_NAMES = [
  'Nursery',
  'LKG',
  'UKG',
  ...Array.from({ length: 12 }, (_, index) => `Class ${index + 1}`),
] as const;

/** A class split more finely than this is a timetable problem, not a class. */
export const MAX_SECTIONS = 10;
/** A section of 500 is a typing slip, not a room. */
export const MAX_SECTION_SEATS = 500;
/** Keeps one class's subject list readable, and the roster row with it. */
export const MAX_CURRICULUM_ITEMS = 25;
/** ₹1 crore a year for one class covers the most expensive institute we sell to. */
export const MAX_FEE = 10_000_000;
/** Nobody is admitted to institute below this age, and nobody sits Class 12 above it. */
export const MIN_ADMISSION_AGE = 2;
export const MAX_ADMISSION_AGE = 25;

/** Indian institute names carry digits, dots, brackets and slashes: "Class 10 (Sr.)". */
const NAME_PATTERN = /^[\p{L}\p{N} .()&/-]+$/u;

/** A reference typed by hand and read back over the phone: no spaces. */
const CODE_PATTERN = /^[A-Za-z0-9-]*$/;

/** How long a class description may run. The server enforces the same 300. */
export const MAX_DESCRIPTION = 300;
/** A class list runs to tens, not thousands — 999 is a slip, not an order. */
export const MAX_DISPLAY_ORDER = 999;

export const CLASS_CATEGORIES = [
  'PRE_PRIMARY',
  'PRIMARY',
  'MIDDLE',
  'SECONDARY',
  'HIGHER_SECONDARY',
] as const;

/**
 * An optional text field, as an `<input>` gives it to us.
 *
 * Blank is how somebody clears one, and it arrives as an empty string from an
 * untouched field — so every one of them becomes `null` here rather than each
 * caller deciding what an empty box meant.
 */
const optionalText = (max: number, tooLong: string) =>
  z
    .string()
    .trim()
    .max(max, tooLong)
    .transform((value) => (value === '' ? null : value));

/**
 * A `<select>` whose blank option is a real answer.
 *
 * "Not stated" for a category is not a missing value, so it is not an error.
 */
const optionalChoice = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .union([z.enum(values), z.literal('')])
    .transform((value) => (value === '' ? null : (value as T[number])));

const seats = z
  .string()
  .trim()
  .min(1, 'Required')
  .regex(/^\d+$/, 'Numbers only')
  .refine(
    (value) => Number(value) >= 1 && Number(value) <= MAX_SECTION_SEATS,
    `Between 1 and ${MAX_SECTION_SEATS} students`,
  )
  .transform(Number);

const fee = z
  .string()
  .trim()
  .min(1, 'Enter an amount')
  .regex(/^\d+$/, 'Whole rupees only')
  .refine((value) => Number(value) <= MAX_FEE, 'That looks too high — check the amount')
  .transform(Number);

/**
 * A fee that may be left blank, which means nothing is charged under it.
 *
 * Distinct from `fee` on purpose: tuition left empty is a question the institute
 * has not answered, while "other fees" left empty is the answer.
 */
const optionalFee = z
  .string()
  .trim()
  .regex(/^\d*$/, 'Whole rupees only')
  .refine((value) => value === '' || Number(value) <= MAX_FEE, 'That looks too high — check the amount')
  .transform((value) => (value === '' ? 0 : Number(value)));

/**
 * One end of the admission age window, in completed years.
 *
 * Blank is a real answer rather than a mistake — it is how an institute says this
 * class has no age rule — so an empty field becomes `null` instead of an error.
 */
const admissionAge = z
  .string()
  .trim()
  .regex(/^\d*$/, 'Whole years only')
  .refine(
    (value) =>
      value === '' ||
      (Number(value) >= MIN_ADMISSION_AGE && Number(value) <= MAX_ADMISSION_AGE),
    `Between ${MIN_ADMISSION_AGE} and ${MAX_ADMISSION_AGE}`,
  )
  .transform((value) => (value === '' ? null : Number(value)));

const sectionSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, 'Name this section')
      .max(20, 'That name is too long')
      .regex(NAME_PATTERN, 'Letters, numbers, spaces and . ( ) & / - only'),
    /** The smallest strength that makes the section worth running. */
    minSeats: seats,
    /** The cap admissions works to, and what every intake total counts. */
    maxSeats: seats,
  })
  // The message lands on the minimum, because that is the field an institute
  // corrects when the two are the wrong way round.
  .refine((section) => section.minSeats <= section.maxSeats, {
    path: ['minSeats'],
    message: 'The minimum cannot be more than the maximum',
  });

/**
 * Core subjects, electives and languages are all "a list of names", so they are
 * validated once. Each entry is an object because the form edits them in place
 * and needs a stable identity per row; the payload flattens back to names.
 */
const curriculumList = z
  .array(
    z.object({
      name: z
        .string()
        .trim()
        .min(2, 'Too short')
        .max(60, 'That name is too long')
        .regex(NAME_PATTERN, 'Letters, numbers, spaces and . ( ) & / - only'),
    }),
  )
  .max(MAX_CURRICULUM_ITEMS, `Up to ${MAX_CURRICULUM_ITEMS} entries`)
  .transform((entries) => entries.map((entry) => entry.name));

export const classSchema = z
  .object({
    /**
     * Picked from `CLASS_NAMES` by the form's dropdown. Not restricted to the
     * list here: a class saved before the list existed keeps its name when it
     * is edited, and the server decides whether a *new* name is on the list.
     */
    name: z
      .string()
      .trim()
      .min(1, 'Choose a class name')
      .max(40, 'That name is too long')
      .regex(NAME_PATTERN, 'Letters, numbers, spaces and . ( ) & / - only'),
    /** Upper-cased on the way out, so "c8" and "C8" cannot both be taken. */
    code: z
      .string()
      .trim()
      .max(20, 'That code is too long')
      .regex(CODE_PATTERN, 'Letters, numbers and hyphens only')
      .transform((value) => (value === '' ? null : value.toUpperCase())),
    category: optionalChoice(CLASS_CATEGORIES),
    medium: optionalText(40, 'That name is too long'),
    description: optionalText(MAX_DESCRIPTION, `Up to ${MAX_DESCRIPTION} characters`),
    iconKey: optionalText(24, 'Not a valid icon'),
    colourKey: optionalText(16, 'Not a valid colour'),
    /** Blank leaves the order to the server, which puts it at the end. */
    displayOrder: z
      .string()
      .trim()
      .regex(/^\d*$/, 'Whole numbers only')
      .refine(
        (value) => value === '' || Number(value) <= MAX_DISPLAY_ORDER,
        `Between 0 and ${MAX_DISPLAY_ORDER}`,
      )
      .transform((value) => (value === '' ? undefined : Number(value))),
    minAgeYears: admissionAge,
    maxAgeYears: admissionAge,
    sections: z
      .array(sectionSchema)
      .min(1, 'A class needs at least one section')
      .max(MAX_SECTIONS, `A class may have at most ${MAX_SECTIONS} sections`)
      .superRefine((sections, ctx) => {
        // Two sections called "A" would make a student's section meaningless.
        const seen = new Map<string, number>();
        sections.forEach((section, index) => {
          const key = section.name.toLowerCase();
          if (seen.has(key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [index, 'name'],
              message: 'Already used by another section',
            });
            return;
          }
          seen.set(key, index);
        });
      }),
    coreSubjects: curriculumList,
    electives: curriculumList,
    languages: curriculumList,
    tuitionFee: fee,
    booksFee: fee,
    otherFees: optionalFee,
  })
  // An age window only means something as a pair, and only in that order.
  .superRefine((values, ctx) => {
    const { minAgeYears: min, maxAgeYears: max } = values;

    if ((min === null) !== (max === null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [min === null ? 'minAgeYears' : 'maxAgeYears'],
        message: 'Set both ages, or leave both blank',
      });
      return;
    }

    if (min !== null && max !== null && min > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['minAgeYears'],
        // Lands on the minimum, because that is the field an institute corrects
        // when the two are the wrong way round.
        message: 'The minimum cannot be more than the maximum',
      });
    }
  });

/** What the form holds — every field a string, as the inputs give them to us. */
export type ClassFormValues = z.input<typeof classSchema>;
/** What a save sends on: numbers as numbers, curriculum lists as plain names. */
export type ClassPayload = z.output<typeof classSchema>;

/**
 * The resolver both class forms use.
 *
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — seats already numbers,
 * curriculum already plain names — while the form state stays strings, so the
 * declared `ClassFormValues` would be a lie and building the payload from them
 * would fail on data that is perfectly valid.
 */
export const classFormResolver: Resolver<ClassFormValues> = zodResolver(classSchema, undefined, {
  raw: true,
});

export type CurriculumFieldName = 'coreSubjects' | 'electives' | 'languages';
